import config from '../../config';
import Cookies from 'js-cookie';
import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import generateShortId from 'app/utilities/shortIds';
import { ICollection } from 'app/store/DMSCollections/DMSCollections.models';
import { login } from 'app/store/Auth/Auth.async';
import { useAppDispatch } from './useStore';
import { getCollections } from 'app/store/DMSCollections/DMSCollections.async';
import { createWorkflow } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.async';
import { createInsuranceCase } from 'app/store/Cases/Cases.async';
import dayjs from 'dayjs';

// --- Interfaces ---
export interface IUserData {
  name: string;
  email: string;
  phone: string;
  company: string;
  password: string;
}

export interface ICaseData {
  claimant: {
    name?: string;
    dateOfBirth?: string | null;
  };
  eventDate?: string | null;
}

export interface ICampaignSettings {
  campaignId: number;
  subscriptionPackageId: string;
  promptIds: string[];
  collectionsNames: string[];
  workflowName: string;
  workflowTrigger: any;
  workflowEnabled: boolean;
  workflowType: string;
  caseName: string;
  caseClaimantName: string;
  caseSettings: Record<string, any>;
  pageData?: Record<string, string>;
  caseLabels?: string[];
}

export interface IError {
  message: string;
}

// --- Error Class ---
export class ResponseError extends Error {
  public name: string;
  public status: number;
  public statusText: string;
  public body: any;
  constructor(
    status: number = 500,
    statusText?: string,
    body?: any
  ) {
    super(statusText);
    this.name = 'ResponseError';
    this.status = status;
    this.statusText = statusText || 'An unknown error occurred';
    this.body = body;
    Object.setPrototypeOf(this, ResponseError.prototype);
  }
}

// --- Error Constants ---
const ERROR_MESSAGES = {
  CAMPAIGN: {
    MISSING_ID: 'Missing Campaign ID: No campaign ID found in URL.',
    ID_MISMATCH: 'Campaign ID Mismatch: Campaign ID from URL does not match settings.',
    FAILED_FETCH_SETTINGS: 'Failed to fetch campaign settings.',
    SETTINGS_NOT_LOADED: 'Cannot sign up without loaded campaign settings.',
  },
  SIGNUP_PROCESS: {
    UNKNOWN_ERROR: 'An unknown error occurred during signup.',
    NETWORK_ERROR: 'Network or unexpected error occurred.',
  },
  API: {
    INVALID_CONTENT_TYPE: 'Invalid content type: Expected JSON response.',
  },
};

export default function useADCampaignSignup() {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const campaignId = location.search ? new URLSearchParams(location.search).get('campaignId') : null;

  const [isSettingsFetching, setIsSettingsFetching] = useState(true);
  const [isRegistrationAllowed, setIsRegistrationAllowed] = useState(false);
  const [campaignSettings, setCampaignSettings] = useState<ICampaignSettings | null>(null);
  const [pageData, setPageData] = useState<ICampaignSettings['pageData'] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ResponseError | null>(null);
  const [success, setSuccess] = useState(false);

  const callApi = useCallback(async <T>(
    endpoint: string,
    init: RequestInit = {},
    accessToken?: string
  ): Promise<T> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...init.headers,
    };
    if (accessToken) (headers as any)['Authorization'] = `Bearer ${accessToken}`;

    try {
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        ...init,
        method: init.method || 'POST',
        headers
      });
      const apiResponse = await handleApiResponse(response);
      if (endpoint === '/users/signup') return apiResponse as T;
      return apiResponse.json() as T;
    } catch (err) {
      throw err instanceof ResponseError
        ? err
        : new ResponseError(500, ERROR_MESSAGES.SIGNUP_PROCESS.NETWORK_ERROR, err);
    }
  }, []);

  useEffect(() => {
    const fetchCampaignSettings = async () => {
      if (!campaignId) {
        setIsRegistrationAllowed(false);
        setError(new ResponseError(400, ERROR_MESSAGES.CAMPAIGN.MISSING_ID));
        setIsSettingsFetching(false);
        return;
      }

      setIsSettingsFetching(true);
      setError(null);

      try {
        const response = await fetch(config.adCampaignSettingsFileUrl);
        if (!isJsonResponse(response)) {
          throw new ResponseError(response.status, ERROR_MESSAGES.API.INVALID_CONTENT_TYPE);
        }

        const data: ICampaignSettings[] = await response.json();
        const campaignSettings = data.find(item => item.campaignId === Number(campaignId));
        if (!campaignSettings) {
          throw new ResponseError(400, ERROR_MESSAGES.CAMPAIGN.ID_MISMATCH);
        }

        setIsRegistrationAllowed(true);
        setCampaignSettings(campaignSettings);
        if (campaignSettings.pageData) {
          setPageData(campaignSettings.pageData);
        }
      } catch (err) {
        setError(err instanceof ResponseError ? err : new ResponseError(500, ERROR_MESSAGES.CAMPAIGN.FAILED_FETCH_SETTINGS, err));
        setIsRegistrationAllowed(false);
      } finally {
        setIsSettingsFetching(false);
      }
    };

    fetchCampaignSettings();
  }, [campaignId]);

  const handleSignup = useCallback(async (data: { caseData: ICaseData; userData: IUserData }, init: RequestInit = {}) => {
    if (!campaignSettings) {
      setError(new ResponseError(400, ERROR_MESSAGES.CAMPAIGN.SETTINGS_NOT_LOADED));
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { caseData, userData } = data;
      const { email, password } = userData;

      await callApi('/users/signup', {
        body: JSON.stringify({ ...userData, subscriptionPackageId: campaignSettings.subscriptionPackageId }),
        ...init
      });

      await dispatch(login({ username: email, password, grantType: 'password' })).unwrap();

      const collections = await dispatch(getCollections({})).unwrap();

      const workflowData = createWorkflowData(campaignSettings, email, collections);
      await dispatch(createWorkflow(workflowData)).unwrap();

      const nextCaseData = createCaseData(campaignSettings, caseData);
      const { id } = await dispatch(createInsuranceCase(nextCaseData)).unwrap();
      Cookies.set('caseId', id.toString(), { expires: 1/24, path: '/' });

      setSuccess(true);
    } catch (err) {
      setError(err instanceof ResponseError ? err : new ResponseError(500, ERROR_MESSAGES.SIGNUP_PROCESS.UNKNOWN_ERROR, err));
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, [campaignSettings, callApi, dispatch]);

  return {
    pageData,
    isRegistrationAllowed,
    isSettingsFetching,
    isLoading,
    error,
    success,
    handleSignup,
  };
}

// --- Helpers ---
async function handleApiResponse(response: Response): Promise<Response> {
  if (!response.ok) {
    const errorBody = await response.json();
    throw new ResponseError(response.status, response.statusText, errorBody);
  }
  return response;
}

const isJsonResponse = (response: Response): boolean => {
  return response.headers.get('content-type')?.includes('application/json') ?? false;
}

const createWorkflowData = (campaignSettings: ICampaignSettings, email: string, collections: ICollection[]) => {
  const { promptIds, workflowName, workflowTrigger, workflowEnabled, workflowType, caseLabels } = campaignSettings;
  const triggerCollectionId = collections.find(collection =>
    collection.applyOcr
  )?.id;
  const documentCollectionId = collections.find(collection => 
    collection.fileFormats && collection.fileFormats.includes('custom')
  )?.id;

  const steps = [];
  const reportDependsOn: string[] = [];

  for (const promptId of promptIds) {
    const runPromptId = generateShortId();
    const documentId = generateShortId();

    steps.push({
      id: runPromptId,
      type: 'task',
      task: 'runPrompt',
      input: { parameters: { promptId, coverage: 1 } },
    });

    steps.push({
      id: documentId,
      type: 'task',
      task: 'createDocument',
      input: { parameters: { collectionId: documentCollectionId } },
      dependsOn: [runPromptId],
    });

    reportDependsOn.push(documentId);
  }

  const reportId = generateShortId();
  steps.push({
    id: reportId,
    type: 'task',
    task: 'createReport',
    dependsOn: reportDependsOn,
  });

  steps.push({
    id: generateShortId(),
    type: 'task',
    task: 'sendEmail',
    input: { parameters: { to: email } },
    dependsOn: [reportId],
  });

  const conditions:any[] = [
    {
      type: 'document:collectionId',
      values: [triggerCollectionId],
    }
  ]

  if (caseLabels?.length) {
    conditions.push({
      type: 'case:labels',
      values: caseLabels,
    });
  }

  return {
    name: workflowName,
    trigger: workflowTrigger,
    conditions,
    enabled: workflowEnabled,
    type: workflowType,
    steps,
  };
}

const createCaseData = (campaignSettings: ICampaignSettings, caseData: ICaseData) => {
  const { caseName, caseClaimantName, caseSettings, caseLabels } = campaignSettings;
  const { claimant, eventDate } = caseData;

  const data:any = {
    name: caseName,
    claimant: { name: caseClaimantName },
    settings: caseSettings
  }

  if (caseLabels?.length) {
    data.labels = caseLabels.map(label => ({ name: label, value: 'blueGrey' }));
  }

  if (eventDate) data['eventDate'] = dayjs(eventDate).format('YYYY-MM-DD');
  if (claimant.dateOfBirth) data.claimant['dateOfBirth'] = dayjs(claimant.dateOfBirth).format('YYYY-MM-DD');
  if (claimant.name) {
    data.claimant['name'] = claimant.name;
    data.name = claimant.name;
  }

  return data;
}
